import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconPrefix, IconName } from '@fortawesome/fontawesome-svg-core';
import { useTranslation } from 'react-i18next';

// Move debounce outside component
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export interface Article {
  id: string;
  name: string;
  product_price_lists: string[];
}

interface ArticleSearchProps {
  onSearch: (term: string) => Promise<Article[]>;
  onSelect: (article: Article) => void;
  disabledIds?: string[];
  searchPlaceholderKey: string;
  searchHelpKey: string;
  searchResultsKey: string;
  idKey: string;
  priceListsKey: string;
  selectButtonIcon?: [IconPrefix, IconName];
  selectButtonVariant?: string;
  onSelectCallback?: () => void;
}

export const ArticleSearch: React.FC<ArticleSearchProps> = ({
  onSearch,
  onSelect,
  disabledIds = [],
  searchPlaceholderKey,
  searchHelpKey,
  searchResultsKey,
  idKey,
  priceListsKey,
  selectButtonIcon = ["fas", "plus"],
  selectButtonVariant = "outline-success",
  onSelectCallback
}) => {
  const [searchResults, setSearchResults] = useState<Article[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const { t } = useTranslation();

  const blurTimeoutRef = useRef<NodeJS.Timeout>();
  const debouncedSearchRef = useRef<((term: string) => void) | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await onSearch(term);
      setSearchResults(results
        .filter(article => !disabledIds.includes(article.id))
        .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (error) {
      console.error('Error searching articles:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [onSearch, disabledIds]);

  // Initialize debounced search function
  useEffect(() => {
    debouncedSearchRef.current = debounce((term: string) => handleSearch(term), 300);
  }, [handleSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearchRef.current?.(value);
  };

  return (
    <div className="mb-3">
      <small className="text-muted d-block mb-2">
        <FontAwesomeIcon icon={["fas", "info-circle"]} className="me-1" />
        {t(searchHelpKey)}
      </small>
      <Form>
        <Form.Control
          type="text"
          placeholder={t(searchPlaceholderKey)}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => {
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current);
            }
            setIsSearchFocused(true);
          }}
          onBlur={() => {
            blurTimeoutRef.current = setTimeout(() => {
              setIsSearchFocused(false);
            }, 200);
          }}
          className="w-100"
        />
        {searchLoading && (
          <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
            <FontAwesomeIcon icon={["fas", "spinner"]} spin />
          </div>
        )}
      </Form>

      {searchResults.length > 0 && isSearchFocused && (
        <div className="mb-3 position-relative">
          <div className="scrollable-container position-absolute w-100" style={{ 
            maxHeight: '450px', 
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderRadius: '0.25rem',
            backgroundColor: '#252525'
          }}>
            <div className="p-2">
              <h6 className="text-muted mb-0">{t(searchResultsKey)}</h6>
            </div>
            {searchResults.map(article => (
              <Card key={article.id} className="mb-2">
                <Card.Body className='p-1'>
                  <Row className="align-items-center">
                    <Col>
                      <div className="d-flex align-items-center">
                        <div className="fw-bold">{article.name}</div>
                      </div>
                      <div className="small text-muted">
                        {t(idKey)}: {article.id}
                        {article.product_price_lists && article.product_price_lists.length > 0 && (
                          <>
                            <br />
                            {t(priceListsKey)}: {article.product_price_lists.join(', ')}
                          </>
                        )}
                      </div>
                    </Col>
                    <Col xs="auto">
                      <Button
                        variant={selectButtonVariant}
                        size="sm"
                        className="border-0"
                        onClick={() => {
                          onSelect(article);
                          setSearchTerm('');
                          setSearchResults([]);
                          onSelectCallback?.();
                        }}
                        disabled={disabledIds.includes(article.id)}
                      >
                        <FontAwesomeIcon icon={selectButtonIcon} />
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 